import { ChevronUp } from 'lucide-react'
import { useState, useEffect } from 'react'

import { cn } from '@/lib/utils'

import { Button } from './Button'

interface ScrollToTopButtonProps {
  /** Scroll position threshold to show button (default: 300px) */
  threshold?: number
  /** Additional classes */
  className?: string
}

/**
 * ScrollToTopButton - Fixed position button to scroll to top
 *
 * Uses shadcn/ui Button with scrollToTop variant from CVA config.
 * Uses ChevronUp icon from lucide-react.
 */
export function ScrollToTopButton({
  threshold = 300,
  className,
}: ScrollToTopButtonProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > threshold)
    }

    window.addEventListener('scroll', toggleVisibility, { passive: true })
    return () => window.removeEventListener('scroll', toggleVisibility)
  }, [threshold])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (!isVisible) return null

  return (
    <Button
      type="button"
      variant="scrollToTop"
      size="scrollToTop"
      onClick={scrollToTop}
      className={cn(className)}
      aria-label="Scroll to top"
    >
      <ChevronUp className="h-8 w-8" />
    </Button>
  )
}

ScrollToTopButton.displayName = 'ScrollToTopButton'
